import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import useJsonLd from "../../hooks/useJsonLd";

export default function CounselingPage() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://example.com";

  useEffect(() => {
    document.title = "無料カウンセリング予約 | ClassLive留学アイルランド";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "アイルランド語学留学・高校留学の無料カウンセリングを受付中。オンライン・電話・LINEで日本語対応。所要30〜60分、手数料一切なし。今すぐ予約できます。");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${siteUrl}/counseling`);
  }, [siteUrl]);

  useJsonLd({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "アイルランド留学 無料カウンセリング",
        "description": "語学留学・高校留学に関する無料個別カウンセリング。オンライン・電話・LINEで日本語対応。所要時間30〜60分。",
        "provider": {
          "@type": "Organization",
          "name": "ClassLive留学アイルランド",
          "url": siteUrl,
          "telephone": "0120-963-815"
        },
        "serviceType": "留学カウンセリング",
        "areaServed": "JP",
        "availableLanguage": "Japanese",
        "url": `${siteUrl}/counseling`,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "JPY",
          "description": "完全無料のカウンセリングサービス"
        }
      },
      {
        "@type": "ContactPage",
        "name": "無料カウンセリング予約 | ClassLive留学アイルランド",
        "url": `${siteUrl}/counseling`,
        "description": "アイルランド留学に関するご相談はすべて無料。語学留学・高校留学・費用・スケジュールなど何でもご相談ください。",
        "inLanguage": "ja"
      }
    ]
  } as Record<string, unknown>);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d6tnl0v5ehaffuo430tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } finally {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/d96573a0121a5bf30e88f74119d4b6ea.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-5 text-sm">
            <Link to="/" className="text-emerald-200 hover:text-white transition-colors cursor-pointer">ホーム</Link>
            <span className="text-white/40">/</span>
            <span className="text-white">無料カウンセリング予約</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white/15 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-calendar-check-line" />
            FREE COUNSELING
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            無料カウンセリング予約
          </h1>
          <p className="text-emerald-100 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            アイルランド留学に関するご相談は、すべて無料です。<br />
            語学留学・高校留学・費用・スケジュールなど、何でもお気軽にご相談ください。
          </p>
          {/* 3 badges */}
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: "ri-money-dollar-circle-line", text: "完全無料" },
              { icon: "ri-time-line", text: "所要時間30〜60分" },
              { icon: "ri-global-line", text: "オンライン対応" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 bg-white/15 text-white text-sm px-4 py-2 rounded-full">
                <i className={`${b.icon} text-base`} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FLOW ── */}
      <section className="py-14 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-center text-lg font-bold text-gray-700 mb-8">カウンセリングの流れ</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: "01", icon: "ri-file-list-3-line", title: "フォーム入力", desc: "下記フォームに必要事項をご入力ください" },
              { step: "02", icon: "ri-mail-send-line", title: "日程確認", desc: "スタッフより2営業日以内にご連絡します" },
              { step: "03", icon: "ri-video-chat-line", title: "カウンセリング", desc: "オンライン・電話でじっくりご相談（30〜60分）" },
              { step: "04", icon: "ri-send-plane-line", title: "プランご提案", desc: "最適な留学プランとお見積りをご提案します" },
            ].map((s, i) => (
              <div key={i} className="relative flex flex-col items-center text-center">
                {i < 3 && (
                  <div className="hidden md:block absolute top-7 left-[calc(50%+28px)] right-0 h-0.5 bg-emerald-200" />
                )}
                <div className="w-14 h-14 flex flex-col items-center justify-center rounded-full bg-emerald-600 text-white mb-3 relative z-10">
                  <i className={`${s.icon} text-lg`} />
                </div>
                <div className="text-xs font-bold text-emerald-600 mb-1">STEP {s.step}</div>
                <div className="text-sm font-bold text-gray-800 mb-1">{s.title}</div>
                <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIN ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

            {/* ── FORM ── */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border-2 border-emerald-100 overflow-hidden">
                <div className="bg-emerald-600 px-6 py-5">
                  <h2 className="text-white font-bold text-lg flex items-center gap-2">
                    <i className="ri-calendar-check-line text-xl" />
                    カウンセリング予約フォーム
                  </h2>
                  <p className="text-emerald-200 text-xs mt-1">ご入力後、担当スタッフより2営業日以内にご連絡いたします</p>
                </div>

                {submitted ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-5">
                      <i className="ri-check-double-line text-3xl" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-xl mb-2">予約申込が完了しました！</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6">
                      ご予約いただきありがとうございます。<br />
                      担当スタッフより<strong>2営業日以内</strong>にご連絡いたします。
                    </p>
                    <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 text-sm text-emerald-800 mb-6">
                      <p className="font-bold mb-2">お急ぎの場合はLINEへ</p>
                      <a
                        href="https://lstep.app/N5D3BZr"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#06C755] text-white px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#05b34c] transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-line-line text-base" />
                        LINEで連絡する
                      </a>
                    </div>
                    <Link
                      to="/"
                      className="text-emerald-600 text-sm hover:underline cursor-pointer"
                    >
                      トップページへ戻る
                    </Link>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="counseling-form"
                    onSubmit={handleSubmit}
                    className="p-6 space-y-5"
                  >
                    {/* Name + Age */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          お名前 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="山田 花子"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          ご相談者の種別
                        </label>
                        <select
                          name="applicant_type"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                        >
                          <option value="">選択してください</option>
                          <option value="本人（大学生・社会人）">本人（大学生・社会人）</option>
                          <option value="保護者（高校留学検討）">保護者（高校留学検討）</option>
                          <option value="本人（高校生）">本人（高校生）</option>
                          <option value="その他">その他</option>
                        </select>
                      </div>
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          メールアドレス <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="example@email.com"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">電話番号</label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="090-0000-0000"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50"
                        />
                      </div>
                    </div>

                    {/* Consultation type */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        ご相談の種類 <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="consultation_type"
                        required
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="語学留学（短期：1〜3ヶ月）">語学留学（短期：1〜3ヶ月）</option>
                        <option value="語学留学（長期：3ヶ月〜）">語学留学（長期：3ヶ月〜）</option>
                        <option value="高校留学">高校留学</option>
                        <option value="ワーキングホリデー">ワーキングホリデー</option>
                        <option value="まだ決めていない">まだ決めていない</option>
                      </select>
                    </div>

                    {/* Format + English level */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">カウンセリング形式</label>
                        <select
                          name="format"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                        >
                          <option value="">選択してください</option>
                          <option value="オンライン（Zoom / Google Meet）">オンライン（Zoom / Google Meet）</option>
                          <option value="電話">電話</option>
                          <option value="LINE通話">LINE通話</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">現在の英語レベル</label>
                        <select
                          name="english_level"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                        >
                          <option value="">選択してください</option>
                          <option value="初心者（英検3級以下）">初心者（英検3級以下）</option>
                          <option value="初中級（英検3〜準2級）">初中級（英検3〜準2級）</option>
                          <option value="中級（英検2級・TOEIC 500〜）">中級（英検2級・TOEIC 500〜）</option>
                          <option value="中上級（英検準1級・TOEIC 700〜）">中上級（英検準1級・TOEIC 700〜）</option>
                          <option value="上級（英検1級・TOEIC 900〜）">上級（英検1級・TOEIC 900〜）</option>
                        </select>
                      </div>
                    </div>

                    {/* Preferred dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                          第1希望 日時 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="datetime-local"
                          name="preferred_date_1"
                          required
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">第2希望 日時</label>
                        <input
                          type="datetime-local"
                          name="preferred_date_2"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                        />
                      </div>
                    </div>

                    {/* Departure timing */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">ご希望の出発時期</label>
                      <select
                        name="departure_timing"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 cursor-pointer"
                      >
                        <option value="">選択してください</option>
                        <option value="3ヶ月以内">3ヶ月以内</option>
                        <option value="半年以内">半年以内</option>
                        <option value="1年以内">1年以内</option>
                        <option value="1年以上先">1年以上先</option>
                        <option value="未定">未定</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        ご質問・ご相談内容
                        <span className="text-gray-400 font-normal ml-2">（{charCount}/500字）</span>
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="留学の目的・期間・気になること・不安なことなど、何でもお書きください（500字以内）"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-400 bg-gray-50 resize-none"
                      />
                      {charCount > 500 && (
                        <p className="text-red-500 text-xs mt-1">500字以内でご入力ください</p>
                      )}
                    </div>

                    {/* Privacy note */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        ご入力いただいた個人情報は、カウンセリングのご連絡および留学サービスに関するご案内にのみ使用いたします。第三者への提供は行いません。
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={charCount > 500}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-full text-base transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-check-line text-lg" />
                      カウンセリングを予約する（無料）
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      送信後、2営業日以内にご連絡いたします。手数料は一切かかりません。
                    </p>
                  </form>
                )}
              </div>
            </div>

            {/* ── SIDEBAR ── */}
            <div className="lg:col-span-2 space-y-5">
              {/* LINE CTA */}
              <div className="bg-emerald-600 rounded-2xl p-6 text-white">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <i className="ri-line-line text-xl" />
                  LINEで気軽に相談
                </p>
                <p className="text-emerald-100 text-xs mb-4 leading-relaxed">
                  フォームより手軽にご相談いただけます。メッセージを送るだけでOK！
                </p>
                <a
                  href="https://lstep.app/N5D3BZr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#06C755] hover:bg-[#05b34c] text-white font-bold px-5 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap w-full"
                >
                  <i className="ri-line-line text-lg" />
                  LINE友だち追加
                </a>
                <div className="mt-4 flex justify-center">
                  <img
                    src="https://static.readdy.ai/image/7e5541a56ec34f9c335c4f691557a0ff/e1baf93312b155fc04e26b58aedc7f5f.png"
                    alt="LINE QR Code"
                    className="w-28 h-28 object-contain bg-white rounded-xl p-1"
                  />
                </div>
              </div>

              {/* What we cover */}
              <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6">
                <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-emerald-600">
                    <i className="ri-chat-check-line text-base" />
                  </span>
                  カウンセリングで相談できること
                </h3>
                <ul className="space-y-2.5">
                  {[
                    "どの語学学校・プログラムが自分に合うか",
                    "留学費用・予算のシミュレーション",
                    "高校留学のTY・シニアサイクルの違い",
                    "ビザ・保険・渡航準備について",
                    "英語レベルに合った留学プランの提案",
                    "現地での生活・治安・滞在環境",
                    "帰国後のキャリア・大学受験への影響",
                    "お子様の留学、保護者のご不安解消",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="w-4 h-4 flex items-center justify-center text-emerald-500 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business hours */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-emerald-600">
                    <i className="ri-time-line text-base" />
                  </span>
                  受付時間
                </h3>
                <ul className="space-y-2 text-sm">
                  {[
                    { day: "平日", time: "10:00 〜 18:00" },
                    { day: "土曜日", time: "10:00 〜 18:00" },
                    { day: "日・祝日", time: "定休日" },
                  ].map((s, i) => (
                    <li key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{s.day}</span>
                      <span className="font-semibold text-gray-800">{s.time}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <i className="ri-phone-line text-emerald-500" />
                    電話：0120-963-815
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
                    <i className="ri-mail-line text-emerald-500" />
                    info@ggjapan.jp
                  </p>
                </div>
              </div>

              {/* Reassurance */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-emerald-600">
                    <i className="ri-shield-check-line text-base" />
                  </span>
                  ご安心ください
                </h3>
                <ul className="space-y-3">
                  {[
                    { icon: "ri-money-dollar-circle-line", text: "相談・カウンセリングは完全無料。しつこい勧誘は一切ありません" },
                    { icon: "ri-user-heart-line", text: "経験豊富な日本人カウンセラーが担当します" },
                    { icon: "ri-lock-line", text: "ご入力の個人情報は厳重に管理いたします" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="w-5 h-5 flex items-center justify-center text-emerald-500 flex-shrink-0 mt-0.5">
                        <i className={item.icon} />
                      </span>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}